import { defineComponent as _defineComponent } from 'vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
  import VideoPlayer from '@/components/VideoPlayer.vue'
  import MediaUploader from '@/components/common/MediaUploader.vue'
  import SVGRenderer from '@/components/common/SVGRenderer.vue'
  import useVideo from '@/composables/useVideo'
  import useEditor from '@/composables/useEditor'
  import useCourse from '@/composables/useCourse'
  import useIcons from '@/composables/useIcons'
  import useBreakpoint from '@/composables/useBreakpoint'
  import { EditorModule } from '@/store/modules/editor'
  import { CourseModule } from '@/store/modules/course'
  import { useRoute } from 'vue-router/composables'
  import type { PropType } from 'vue'
  import type {
    CourseModule as CourseModuleType,
    EditorSlide,
    EditorStatePayload,
    Lesson,
    LessonDownloadable,
    LessonQuestion,
  } from '@/services/interfaces/Course'
  import type { Nullable } from '@/services/interfaces/Content'

  
export default /*#__PURE__*/_defineComponent({
  __name: 'VideoOrImageRenderer',
  props: {
    fileType: {
      required: true,
      type: String,
    },
    videoOnly: {
      type: Boolean,
      default: false,
    },
    imageOnly: {
      type: Boolean,
      default: false,
    },
    trackImageData: {
      type: Boolean,
      default: false,
    },
    hasImageShadow: {
      type: Boolean,
      default: false,
    },
    videoUrl: String,
    imageUrl: String,
    thumbnailTimestamp: Number,
    canEdit: {
      default: true,
      type: Boolean,
    },
    editorState: String as PropType<'currentLesson' | 'currentModule' | 'lessonDownloadable' | string>,
    editorProperty: String as PropType<
      keyof Lesson | keyof CourseModuleType | keyof LessonQuestion | keyof EditorSlide | keyof LessonDownloadable
    >,
    mutateAndQueueFunc: Function as PropType<(data: EditorStatePayload) => void>,
  },
  emits: [
    'remove-video-url',
    'remove-image-url',
    'image-data',
    'video-url',
    'uploaded-image',
    'download-document',
  ],
  setup(__props, { emit }) {

const props = __props;

  

  

  const route = useRoute()
  const { getPosterImage } = useVideo()
  const { currentCourseId, moduleId, lessonId, currentModule, currentLesson } = useCourse()
  const { inEditorMode: editorMode } = useEditor(route)
  const { download } = useIcons()
  const { isMobileDevice } = useBreakpoint()
  const imageUploadPreviewUrl = ref(props.imageUrl || '')
  const imageUploadData = ref('')
  const uploadVideoUrl = ref(props.videoUrl || '')
  const hoveringImage = ref(false)

  const inEditorMode = computed(() => editorMode.value && props.canEdit)

  const hasImageOrVideoUrl = computed(() => {
    return !!(contentVideoUrl.value || contentImageUrl.value)
  })

  const contentVideoUrl = computed(() => {
    if (inEditorMode.value) return uploadVideoUrl.value
    return props.videoUrl
  })

  const contentImageUrl = computed(() => {
    if (inEditorMode.value) return imageUploadPreviewUrl.value
    return props.imageUrl
  })

  const onRemoveTeaser = () => {
    if (contentVideoUrl.value) {
      emit('remove-video-url')
      uploadVideoUrl.value = ''
    } else if (contentImageUrl.value) {
      emit('remove-image-url')
      imageUploadPreviewUrl.value = ''
    }
  }

  const onImageUpload = (imageData: string, previewUrl: string) => {
    emit('image-data', imageData)
    emit('uploaded-image')
    imageUploadPreviewUrl.value = previewUrl
    imageUploadData.value = imageData
  }

  const onVideoUpload = (url: string) => {
    emit('video-url', url)
    uploadVideoUrl.value = url
  }

  const hoveringDownloadDocument = (hovering: boolean) => {
    if (props.fileType === 'lesson_downloadable' && !inEditorMode.value) {
      hoveringImage.value = hovering
    }
  }

  const discardChangesCallback = (stateBeforeChange?: {
    currentLesson: Nullable<Lesson>
    currentModule: Nullable<CourseModuleType>
    lessonDownloadable: Nullable<LessonDownloadable>
  }) => {
    if (uploadVideoUrl.value) {
      uploadVideoUrl.value = (stateBeforeChange?.[props.editorState as keyof typeof stateBeforeChange] as any)?.[
        props.editorProperty!
      ]
    } else if (imageUploadPreviewUrl.value) {
      imageUploadPreviewUrl.value = (stateBeforeChange?.[props.editorState as keyof typeof stateBeforeChange] as any)?.[
        props.editorProperty!
      ]
    }
  }

  const updateStateModel = (data: EditorStatePayload) => {
    if (uploadVideoUrl.value) {
      uploadVideoUrl.value = data.value as string
    } else if (imageUploadPreviewUrl.value) {
      imageUploadPreviewUrl.value = data.value as string
    }
  }

  const uploadVideoUrlOrImageUploadPreviewUrl = (url: string) => {
    if (!props.editorProperty || !props.editorState) return

    const isImageWithTrackData =
      props.trackImageData && ['teaserImageUrl', 'profileImageUrl', 'imageUrl'].includes(props.editorProperty)
    const isDownloadTeaser = props.editorProperty === 'downloadTeaserImageUrl'

    const stateValue = isImageWithTrackData || isDownloadTeaser ? { url, imageData: imageUploadData.value } : url

    EditorModule.setEditableState({
      key: `${props.editorState}${props.editorProperty}`,
      path: route.path,
      state: props.editorState,
      property: props.editorProperty,
      value: stateValue,
      mutateAndQueue: props.mutateAndQueueFunc,
    })
  }

  watch(
    () => props.imageUrl,
    (url) => {
      imageUploadPreviewUrl.value = url as string
    },
  )

  watch(imageUploadPreviewUrl, (url) => {
    uploadVideoUrlOrImageUploadPreviewUrl(url)
  })

  watch(
    () => props.videoUrl,
    (url) => {
      uploadVideoUrl.value = url as string
    },
  )

  watch(uploadVideoUrl, (url) => {
    uploadVideoUrlOrImageUploadPreviewUrl(url)
  })

  onMounted(() => {
    EditorModule.subscribe({
      type: 'discard',
      key: `${props.editorState}${props.editorProperty}`,
      callback: () =>
        discardChangesCallback({
          currentModule: currentModule.value as CourseModuleType,
          currentLesson: currentLesson.value,
          lessonDownloadable: CourseModule.currentLesson?.downloadables ?? null,
        }),
    })
    EditorModule.subscribe({
      type: 'save',
      key: `${props.editorState}${props.editorProperty}`,
      callback: updateStateModel,
    })
  })

  onBeforeUnmount(() => {
    EditorModule.unsubscribe({ type: 'discard', callback: discardChangesCallback })
  })

return { __sfc: true,props, emit, route, getPosterImage, currentCourseId, moduleId, lessonId, currentModule, currentLesson, editorMode, download, isMobileDevice, imageUploadPreviewUrl, imageUploadData, uploadVideoUrl, hoveringImage, inEditorMode, hasImageOrVideoUrl, contentVideoUrl, contentImageUrl, onRemoveTeaser, onImageUpload, onVideoUpload, hoveringDownloadDocument, discardChangesCallback, updateStateModel, uploadVideoUrlOrImageUploadPreviewUrl, computed, onBeforeUnmount, onMounted, ref, watch, VideoPlayer, MediaUploader, SVGRenderer, useVideo, useEditor, useCourse, useIcons, useBreakpoint, EditorModule, CourseModule, useRoute }
}

})